import {
  useCallback, useContext, useEffect, useState,
} from "react";

import { ApolloError, useLazyQuery, useMutation } from "@apollo/client";
import { FormTypeEnum } from "@layout/modals/types";
import { convertDateForFront, setAuthParamToURL } from "@layout/modals/utils";
import { navigate } from "gatsby";

import { getRedirectUrlSessionStorage, setRedirectUrlSessionStorage } from "@/account/redirect";
import { setAccessToken, setCookieAccepted, setRefreshToken } from "@/account/user";
import UserContext from "@/contexts/User/UserContext";
import { getMetricaCID } from "@/marketing/yandexMetrika";

import { authorize as authorizeType, authorizeVariables } from "./graphql/__generated__/authorize";
import { authorizeByToken as authorizeByTokenType, authorizeByTokenVariables } from "./graphql/__generated__/authorizeByToken";
import { getUserQuestionnaireInfo } from "./graphql/__generated__/getUserQuestionnaireInfo";
import { AUTHORIZE_MUTATION } from "./graphql/AUTHORIZE";
import { AUTHORIZE_BY_TOKEN } from "./graphql/AUTHORIZE_BY_TOKEN";
import { GET_USER_QUESTIONNAIRE_INFO } from "./graphql/GET_USER_QUESTIONNAIRE_INFO";

const useAuthorize = (
  onSuccess: () => void,
  onError: (authError: ApolloError) => void,
) => {
  const {
    setUserID,
    setEmail,
    setIsEmailVerified,
    setPhone,
    setName,
    setDateOfBirth,
    promoUnauthorizedUser,
  } = useContext(UserContext);
  const [isFirstAuthorization, setIsFirstAuthorization] = useState(false);
  const [isMetricaCIDLoading, setIsMetricaCIDLoading] = useState(false);

  const [authorizeUser, {
    data: authData,
    loading: authLoading,
    error: authError,
  }] = useMutation<
    authorizeType,
    authorizeVariables
  >(AUTHORIZE_MUTATION);
  const [authorizeByTokenMutation, {
    data: authByTokenData,
    loading: authByTokenLoading,
    error: authByTokenError,
  }] = useMutation<authorizeByTokenType, authorizeByTokenVariables>(AUTHORIZE_BY_TOKEN);
  const [
    getUserQuestionnaireInfoQuery,
    {
      data: questionnaireInfoData,
      loading: questionnaireInfoLoading,
      error: questionnaireInfoError,
    },
  ] = useLazyQuery<getUserQuestionnaireInfo>(
    GET_USER_QUESTIONNAIRE_INFO,
  );

  const authorize = useCallback((loginInfo: authorizeVariables) => {
    if (loginInfo) {
      // ClientID нужен бэку в заголовке запроса входа для цели «Регистрация».
      setIsMetricaCIDLoading(true);
      getMetricaCID().then(() => {
        setIsMetricaCIDLoading(false);
        authorizeUser({ variables: loginInfo });
      });
    } else {
      navigate("/catalog");
    }
  }, [authorizeUser]);

  const authorizeByToken = useCallback((loginInfo: authorizeByTokenVariables) => {
    if (loginInfo) {
      // ClientID нужен бэку в заголовке запроса входа для цели «Регистрация».
      setIsMetricaCIDLoading(true);
      getMetricaCID().then(() => {
        setIsMetricaCIDLoading(false);
        authorizeByTokenMutation({ variables: loginInfo });
      });
    } else {
      navigate("/catalog");
    }
  }, [authorizeByTokenMutation]);

  useEffect(() => {
    if ((authData && !authLoading && !authError)
      || (authByTokenData && !authByTokenLoading && !authByTokenError)
    ) {
      const { isFirstAuth, userID, tokens } = authData
        ? authData.authorize
        : authByTokenData!.authorize;

      if (tokens) {
        if (isFirstAuth) {
          setIsFirstAuthorization(true);
        }
        setUserID(userID);
        setAccessToken(tokens.accessToken);
        setRefreshToken(tokens.refreshToken);

        setCookieAccepted(true);

        getUserQuestionnaireInfoQuery();
        onSuccess();
      }
    }

    if (authError) {
      onError(authError);
    }
  }, [authByTokenData, authByTokenLoading, authByTokenError, authData, authLoading,
    authError, promoUnauthorizedUser, setUserID, getUserQuestionnaireInfoQuery,
    onSuccess, onError]);

  useEffect(() => {
    if (questionnaireInfoData && !questionnaireInfoLoading && !questionnaireInfoError) {
      const {
        isEmailVerified,
        email,
        phone,
        user: {
          name,
          dateOfBirth,
          freeMinutesLeft,
        },
      } = questionnaireInfoData.getMyProfile;
      setEmail(email);
      setIsEmailVerified(!!isEmailVerified);
      setPhone(phone || "");
      setName(name);
      setDateOfBirth(convertDateForFront(dateOfBirth));
      // todo: maybe call setFreeMinutesCount(freeMinutesLeft)

      if (email) { // Auth by email
        const redirectUrl = getRedirectUrlSessionStorage() || "";
        if (isFirstAuthorization) {
          setAuthParamToURL(window.location, FormTypeEnum.BirthDateQuestionnaire);
        } else if (redirectUrl) {
          setAuthParamToURL(window.location, null);
          navigate(redirectUrl);
          setRedirectUrlSessionStorage("");
        } else {
          setAuthParamToURL(window.location, null);
        }
      } else if (!email) {
        setAuthParamToURL(window.location, FormTypeEnum.Questionnaire);
      } else {
        setAuthParamToURL(window.location, null);
      }
    }
  }, [
    questionnaireInfoData,
    questionnaireInfoLoading,
    questionnaireInfoError,
    isFirstAuthorization,
    setEmail,
    setIsEmailVerified,
    setPhone,
    setName,
    setDateOfBirth,
  ]);

  return { authorize, authorizeByToken, loading: authLoading || authByTokenLoading || isMetricaCIDLoading };
};

export default useAuthorize;
